import React, { useState, useEffect } from 'react';
import * as documentStorage from '../services/documentStorage';
import { getFirestore, doc, getDoc } from 'firebase/firestore';

export default function DocumentManager({ user, darkMode }) {
  const [documents, setDocuments] = useState([]);
  const [transcripts, setTranscripts] = useState([]);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('documents'); // 'documents' | 'transcripts'
  const [subscriptionTier, setSubscriptionTier] = useState('free');

  const theme = {
    card: darkMode ? 'rgba(49, 46, 129, 0.4)' : 'rgba(255, 255, 255, 0.85)',
    border: darkMode ? 'rgba(224, 231, 255, 0.15)' : 'rgba(30, 41, 59, 0.1)',
    text: darkMode ? '#ffffff' : '#1e293b',
    subtext: darkMode ? '#e0e7ff' : '#64748b',
    accent: darkMode ? '#818cf8' : '#8b5cf6',
    success: '#10b981',
    error: '#ef4444',
    warning: '#f59e0b'
  };

  useEffect(() => {
    if (user?.uid) {
      loadSubscriptionTier();
    }
  }, [user]);

  useEffect(() => {
    if (user?.uid && subscriptionTier) {
      loadData();
    }
  }, [user, subscriptionTier]);

  async function loadSubscriptionTier() {
    try {
      const db = getFirestore();
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        setSubscriptionTier(data.subscriptionTier || 'free');
      }
    } catch (error) {
      console.error('Error loading subscription tier:', error);
    }
  }

  function loadData() {
    const docs = documentStorage.getDocuments(user.uid);
    const trans = documentStorage.getTranscripts(user.uid);
    const storageStats = documentStorage.getStorageStats(user.uid, subscriptionTier);
    
    setDocuments(docs);
    setTranscripts(trans);
    setStats(storageStats);
  }

  function handleDeleteDocument(docId) {
    if (window.confirm('Are you sure you want to delete this document?')) {
      documentStorage.deleteDocument(user.uid, docId);
      loadData();
    }
  }

  function handleToggleDocumentMemory(docId, currentState) {
    documentStorage.toggleDocumentMemory(user.uid, docId, !currentState);
    loadData();
  }

  function handleDeleteTranscript(transcriptId) {
    if (window.confirm('Are you sure you want to delete this transcript?')) {
      documentStorage.deleteTranscript(user.uid, transcriptId);
      loadData();
    }
  }

  function handleToggleTranscriptMemory(transcriptId, currentState) {
    documentStorage.toggleTranscriptMemory(user.uid, transcriptId, !currentState);
    loadData();
  }

  function formatDate(dateString) {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  function formatFileSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  return (
    <div style={{
      background: theme.card,
      border: `1px solid ${theme.border}`,
      borderRadius: '16px',
      padding: '24px',
      color: theme.text
    }}>
      <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '8px' }}>
        Documents & Transcripts
      </h3>
      <p style={{ fontSize: '14px', color: theme.subtext, marginBottom: '24px' }}>
        Manage your uploaded documents and conversation transcripts. Toggle whether each item is included in your coach's memory.
      </p>

      {/* Stats */}
      {stats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '12px',
          marginBottom: '24px',
          padding: '16px',
          background: darkMode ? 'rgba(129, 140, 248, 0.1)' : 'rgba(139, 92, 246, 0.1)',
          borderRadius: '12px'
        }}>
          <div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: theme.accent }}>
              {stats.totalDocs}
            </div>
            <div style={{ fontSize: '12px', color: theme.subtext }}>
              Documents ({stats.docsInMemory} in memory)
            </div>
          </div>
          <div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: theme.accent }}>
              {stats.totalTranscripts}
            </div>
            <div style={{ fontSize: '12px', color: theme.subtext }}>
              Transcripts ({stats.transcriptsInMemory} in memory)
            </div>
          </div>
          <div>
            <div style={{ 
              fontSize: '24px', 
              fontWeight: '700', 
              color: stats.isAtLimit ? theme.error : (stats.isNearLimit ? theme.warning : theme.success)
            }}>
              {stats.totalSizeMB} MB
            </div>
            <div style={{ fontSize: '12px', color: theme.subtext }}>
              {stats.totalSizeMB} / {stats.limitMB} MB ({stats.usagePercent}%)
            </div>
            {stats.isAtLimit && (
              <div style={{ fontSize: '11px', color: theme.error, marginTop: '4px' }}>
                ⚠️ Storage limit reached
              </div>
            )}
            {stats.isNearLimit && !stats.isAtLimit && (
              <div style={{ fontSize: '11px', color: theme.warning, marginTop: '4px' }}>
                ⚠️ Near limit ({stats.remainingMB} MB remaining)
              </div>
            )}
          </div>
        </div>
      )}

      {/* Storage Limit Warning */}
      {stats && (stats.isAtLimit || stats.isNearLimit) && (
        <div style={{
          marginBottom: '24px',
          padding: '12px 16px',
          background: stats.isAtLimit ? 'rgba(239, 68, 68, 0.1)' : 'rgba(245, 158, 11, 0.1)',
          border: `1px solid ${stats.isAtLimit ? theme.error : theme.warning}`,
          borderRadius: '8px',
          fontSize: '13px',
          color: stats.isAtLimit ? theme.error : theme.warning
        }}>
          {stats.isAtLimit ? (
            <>
              <strong>⚠️ Storage Limit Reached</strong>
              <p style={{ margin: '4px 0 0 0' }}>
                You've reached your {stats.subscriptionTier} plan limit of {stats.limitMB} MB. 
                Please delete some files or{' '}
                <a href="/subscription" style={{ color: theme.accent, textDecoration: 'underline' }}>
                  upgrade your plan
                </a> to get more storage.
              </p>
            </>
          ) : (
            <>
              <strong>⚠️ Storage Warning</strong>
              <p style={{ margin: '4px 0 0 0' }}>
                You're using {stats.usagePercent}% of your storage ({stats.remainingMB} MB remaining). 
                Consider deleting old files or{' '}
                <a href="/subscription" style={{ color: theme.accent, textDecoration: 'underline' }}>
                  upgrading your plan
                </a> for more space.
              </p>
            </>
          )}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: `2px solid ${theme.border}` }}>
        <button
          onClick={() => setActiveTab('documents')}
          style={{
            padding: '12px 20px',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'documents' ? `3px solid ${theme.accent}` : 'none',
            color: activeTab === 'documents' ? theme.accent : theme.subtext,
            fontWeight: activeTab === 'documents' ? '600' : '400',
            cursor: 'pointer',
            marginBottom: '-2px'
          }}
        >
          Documents ({documents.length})
        </button>
        <button
          onClick={() => setActiveTab('transcripts')}
          style={{
            padding: '12px 20px',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'transcripts' ? `3px solid ${theme.accent}` : 'none',
            color: activeTab === 'transcripts' ? theme.accent : theme.subtext,
            fontWeight: activeTab === 'transcripts' ? '600' : '400',
            cursor: 'pointer',
            marginBottom: '-2px'
          }}
        >
          Transcripts ({transcripts.length})
        </button>
      </div>

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <div>
          {documents.length === 0 ? (
            <p style={{ color: theme.subtext, textAlign: 'center', padding: '40px 20px' }}>
              No documents uploaded yet. Upload documents in the chat to get started!
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {documents.map(doc => (
                <div key={doc.id} style={{
                  padding: '16px',
                  border: `1px solid ${theme.border}`,
                  borderRadius: '12px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '12px'
                }}>
                  <div style={{ flex: '1', minWidth: '200px' }}>
                    <div style={{ fontWeight: '600', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      📄 {doc.name}
                      {doc.inCoachMemory && (
                        <span style={{
                          padding: '2px 8px',
                          background: theme.success,
                          color: 'white',
                          borderRadius: '12px',
                          fontSize: '11px'
                        }}>
                          In Memory
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '13px', color: theme.subtext }}>
                      {formatFileSize(doc.size)} • {formatDate(doc.uploadedAt)}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => handleToggleDocumentMemory(doc.id, doc.inCoachMemory)}
                      style={{
                        padding: '6px 12px',
                        background: doc.inCoachMemory ? theme.warning : theme.success,
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                    >
                      {doc.inCoachMemory ? 'Remove from Memory' : 'Add to Memory'}
                    </button>
                    <button
                      onClick={() => handleDeleteDocument(doc.id)}
                      style={{
                        padding: '6px 12px',
                        background: theme.error,
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Transcripts Tab */}
      {activeTab === 'transcripts' && (
        <div>
          {transcripts.length === 0 ? (
            <p style={{ color: theme.subtext, textAlign: 'center', padding: '40px 20px' }}>
              No transcripts saved yet. Transcripts are automatically saved every 5 messages.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {transcripts.slice().reverse().map(transcript => (
                <div key={transcript.id} style={{
                  padding: '16px',
                  border: `1px solid ${theme.border}`,
                  borderRadius: '12px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '12px'
                }}>
                  <div style={{ flex: '1', minWidth: '200px' }}>
                    <div style={{ fontWeight: '600', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      💬 {transcript.title}
                      {transcript.inCoachMemory && (
                        <span style={{
                          padding: '2px 8px',
                          background: theme.success,
                          color: 'white',
                          borderRadius: '12px',
                          fontSize: '11px'
                        }}>
                          In Memory
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '13px', color: theme.subtext }}>
                      {transcript.messageCount} messages • {formatDate(transcript.createdAt)}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => handleToggleTranscriptMemory(transcript.id, transcript.inCoachMemory)}
                      style={{
                        padding: '6px 12px',
                        background: transcript.inCoachMemory ? theme.warning : theme.success,
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                    >
                      {transcript.inCoachMemory ? 'Remove from Memory' : 'Add to Memory'}
                    </button>
                    <button
                      onClick={() => handleDeleteTranscript(transcript.id)}
                      style={{
                        padding: '6px 12px',
                        background: theme.error,
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Info Notice */}
      <div style={{
        marginTop: '20px',
        padding: '12px',
        background: darkMode ? 'rgba(129, 140, 248, 0.1)' : 'rgba(139, 92, 246, 0.1)',
        borderRadius: '8px',
        fontSize: '13px',
        color: theme.subtext
      }}>
        💡 <strong>Tip:</strong> Documents and transcripts marked "In Memory" are automatically included in your coach's context. Your coach can reference them in conversations.
      </div>
    </div>
  );
}

